import { FormInstrumentTitle } from "@/features/pages/components/FormInstrumentTitle";
import {
    formPageColumnClass,
    formPageFooterActionsClass,
    formPageFooterFrameClass,
    formPageShellFillClass,
} from "@/features/pages/components/formPageLayout";
import { cn } from "@/lib/utils";
import { FormInstructionsCard, FormMetrics } from "@patient-watch/forms";
import { Button } from "@patient-watch/ui/button";
import { useMatches } from "@tanstack/react-router";

interface FormPublicCoverProps {
    name?: string | null;
    caption?: string | null;
    kicker?: string | null;
    copyright?: string | null;
    instructions?: string | null;
    timeToComplete?: number | null;
    numberOfQuestions?: number | null;
    /** From forms.title_align. */
    titleAlign?: "left" | "center";
    /** From forms.show_cover_metrics. Off hides the time and question count. */
    showMetrics?: boolean;
    startLabel: string;
    isStarting?: boolean;
    onStart: () => void;
    /** Fill a fixed frame, such as the design phone, instead of the viewport. */
    constrain?: boolean;
}

/** Public start page at /forms/$formId. Time and question count only, unless the form hides them. */
export function FormPublicCover({
    name,
    caption,
    kicker,
    copyright,
    instructions,
    timeToComplete,
    numberOfQuestions,
    titleAlign = "left",
    showMetrics = true,
    startLabel,
    isStarting = false,
    onStart,
    constrain = false,
}: FormPublicCoverProps) {
    const isAppRoute = useMatches().some((match) => match.routeId === "/_app");

    return (
        <div
            className={cn(
                constrain
                    ? "flex flex-col h-full min-h-0 overflow-y-auto"
                    : isAppRoute
                      ? formPageShellFillClass
                      : formPageColumnClass
            )}
            data-testid="form-cover"
            data-form-text
        >
            <div className="flex flex-col flex-1 gap-6">
                <FormInstrumentTitle name={name} caption={caption} kicker={kicker} variant="cover" align={titleAlign} />
                {showMetrics ? (
                    <FormMetrics
                        timeToComplete={timeToComplete || null}
                        numberOfQuestions={numberOfQuestions || null}
                        variant="horizontal"
                        size="sm"
                    />
                ) : null}
                {instructions ? <FormInstructionsCard instructions={instructions} variant="compact" /> : null}
            </div>
            <div className="shrink-0">
                {copyright ? (
                    <p data-anno="copyright" className="pt-6 text-muted-foreground text-lg">
                        {copyright}
                    </p>
                ) : null}
                <div className={formPageFooterFrameClass}>
                    <div className={formPageFooterActionsClass}>
                        <div className="footer-actions-primary">
                            <Button
                                className="px-4 w-full"
                                data-testid="form-start-button"
                                onClick={onStart}
                                disabled={isStarting}
                            >
                                {startLabel}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
